import type Location from '@/models/location';
import { debug } from '@/utils/logger';

const TTL_MS = 10 * 60 * 1000;

/** Matches backend `normalizeParseField`: trim, then collapse any whitespace run to one space. */
export function normalizeParseLocationsKeyPart(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

/** Request body fields sent to parse_locations (must match backend normalization). */
export function normalizeParseLocationsRequestBody(title: string, body: string): {
  title: string;
  body: string;
} {
  return {
    title: normalizeParseLocationsKeyPart(title),
    body: normalizeParseLocationsKeyPart(body),
  };
}

export function buildParseLocationsCacheKey(
  title: string,
  body: string,
  photoFormat = 'identifiers',
): string {
  const normalized = normalizeParseLocationsRequestBody(title, body);
  return `${normalized.title}\u0000${normalized.body}\u0000${photoFormat}`;
}

type CachedEntry = { expiresAt: number; value: Location[] };

const resultCache = new Map<string, CachedEntry>();
const inFlight = new Map<string, Promise<Location[]>>();

export type ParseLocationsPostFn = (
  body: { title: string; body: string },
  photoFormat: string,
) => Promise<Location[]>;

export async function postParseLocationsWithDedupe(
  params: { title: string; body: string; photoFormat?: string },
  postFn: ParseLocationsPostFn,
): Promise<Location[]> {
  const photoFormat = params.photoFormat ?? 'identifiers';
  const { title, body } = normalizeParseLocationsRequestBody(params.title, params.body);
  const key = buildParseLocationsCacheKey(title, body, photoFormat);
  const now = Date.now();

  const cached = resultCache.get(key);
  if (cached && cached.expiresAt > now) {
    debug('[parseLocationsRequestCache] memory hit');
    return cached.value;
  }

  const pending = inFlight.get(key);
  if (pending) {
    return pending;
  }

  const promise = postFn({ title, body }, photoFormat)
    .then((locations) => {
      // Backend does not store empty parse results. Caching [] here would hide a retry
      // for 10 minutes in this tab.
      if (locations.length > 0) {
        resultCache.set(key, { expiresAt: Date.now() + TTL_MS, value: locations });
      }
      inFlight.delete(key);
      return locations;
    })
    .catch((err) => {
      inFlight.delete(key);
      throw err;
    });

  inFlight.set(key, promise);
  return promise;
}

/** @internal Vitest only */
export function resetParseLocationsRequestCacheForTests(): void {
  resultCache.clear();
  inFlight.clear();
}
